import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useRecords, useRefreshCounts } from "../hooks/useRecords";
import { useQueryClient } from "@tanstack/react-query";
import { apiSend } from "../lib/api";
import { expandTwinCheckRanges, isoWeeksAgo, isoToday } from "../lib/format";
import type { OrderRecord } from "../lib/types";

type RowState = "completed" | "no-show" | "";

function bucketToState(bucket?: string): RowState {
  if (bucket === "picked" || bucket === "tossed" || bucket === "mailed") return "completed";
  if (bucket === "no-show") return "no-show";
  return "";
}
function valueToState(value: string): RowState {
  const v = value.toLowerCase();
  if (value === "no show - tossed") return "no-show";
  if (v.includes("picked") || v.includes("tossed") || v.includes("mailed")) return "completed";
  return "";
}
function rowClass(s: RowState): string {
  if (s === "no-show") return "no-show-order";
  if (s === "completed") return "completed-order";
  return "";
}

// Prints <select> className + blank label, ported from renderPrintsSelect().
function printsSelectClass(record: OrderRecord): { cls: string; blankLabel: string } {
  const status = (record["print-dispatch-status"] as string) || "";
  const orderedRaw = String(record.prints == null ? "" : record.prints).toLowerCase();
  const ordered =
    !!orderedRaw &&
    orderedRaw !== "no" &&
    orderedRaw !== "n" &&
    orderedRaw !== "null" &&
    orderedRaw.indexOf("no thanks") === -1;
  let cls = "editable-field orders-field prints-select";
  if (status === "pickup") cls += " prints-pickup";
  else if (status === "dropship-pending") cls += " prints-dropship-pending";
  else if (status === "dropship-complete") cls += " prints-dropship-complete";
  else if (ordered) cls += " prints-uncategorized";
  return { cls, blankLabel: ordered ? "— prints: set path —" : "—" };
}

export function OrdersTab({ active, onOpenHistory }: { active: boolean; onOpenHistory: (id: string, name?: string) => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const refreshCounts = useRefreshCounts();

  // Date range: default last 8 weeks (set on first render, like loadAllOrders).
  const [start, setStart] = useState(() => isoWeeksAgo(8));
  const [end, setEnd] = useState(() => isoToday());
  const [range, setRange] = useState<{ start: string; end: string } | null>({ start: isoWeeksAgo(8), end: isoToday() });
  const [find, setFind] = useState("");
  // Per-row status override after an inline return-status edit (row recolor).
  const [overrides, setOverrides] = useState<Record<string, RowState>>({});

  const { data, isLoading, error } = useRecords("orders", range);
  const records = data?.records ?? [];

  const apply = () => setRange(start && end ? { start, end } : null);
  const showAll = () => {
    setStart("");
    setEnd("");
    setRange(null);
  };
  const clear = () => {
    const s = isoWeeksAgo(8);
    const e = isoToday();
    setStart(s);
    setEnd(e);
    setFind("");
    setRange({ start: s, end: e });
  };

  async function saveField(record: OrderRecord, fieldName: string, el: HTMLInputElement | HTMLSelectElement) {
    const id = String(record.rowid);
    let value = el.value;
    if (fieldName === "Twin Check Stickers") {
      value = expandTwinCheckRanges(value);
      el.value = value;
    }
    try {
      await apiSend("update-record", "POST", { id, field: fieldName, value });
      el.classList.add("field-saved");
      setTimeout(() => el.classList.remove("field-saved"), 500);
      if (fieldName === "return-status") {
        setOverrides((o) => ({ ...o, [id]: valueToState(value) }));
      }
      refreshCounts();
    } catch (err) {
      console.error("Error updating field:", err);
      alert("Failed to update field");
    }
  }

  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>, record: OrderRecord, fieldName: string) {
    if (e.key === "Enter") {
      e.preventDefault();
      const el = e.currentTarget;
      saveField(record, fieldName, el);
      const currentRow = el.closest("tr");
      const nextRow = currentRow?.nextElementSibling as HTMLElement | null;
      const nextField = nextRow?.querySelector<HTMLInputElement>(`[data-field="${fieldName}"]`);
      if (nextField) {
        nextField.focus();
        nextField.select();
      }
    } else if (e.key === "Tab") {
      saveField(record, fieldName, e.currentTarget);
    }
  }

  async function deleteRecord(id: string) {
    if (!confirm("Are you sure you want to delete this record?")) return;
    try {
      await apiSend("delete-record", "POST", { id });
      qc.invalidateQueries({ queryKey: ["records", "orders"] });
      refreshCounts();
    } catch (err) {
      console.error("Error deleting record:", err);
      alert("Failed to delete record");
    }
  }

  function viewOrder(record: OrderRecord) {
    const id = String(record["invoice-id"] || record.rowid);
    navigate({ to: "/orders/$invoiceId", params: { invoiceId: id } });
  }

  // find-in-page highlight (matches findInPage()).
  const findLower = find.trim().toLowerCase();

  return (
    <div className="tab-content" style={{ display: active ? undefined : "none" }}>
      <div className="orders-controls">
        <input
          type="text"
          placeholder="Search this page…"
          value={find}
          onChange={(e) => setFind(e.target.value)}
        />
        <label style={{ marginLeft: 10, color: "#666" }}>From:</label>
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        <label style={{ marginLeft: 5, color: "#666" }}>To:</label>
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        <button onClick={apply}>Apply</button>
        <button onClick={clear}>Clear Filters</button>
        <button onClick={showAll} style={{ marginLeft: 10, backgroundColor: "#37358F" }}>
          Show All Orders
        </button>
      </div>
      <div className="table-container">
        <table id="ordersTable">
          <thead>
            <tr>
              <th style={{ width: 35 }}>#</th>
              <th style={{ width: 90 }}>Date</th>
              <th>Customer Name</th>
              <th>Roll Types</th>
              <th style={{ width: 60 }}>Amt</th>
              <th style={{ width: 280 }}>Twin Check</th>
              <th>Return Option</th>
              <th>Status</th>
              <th>Notes</th>
              <th style={{ width: 150 }}>Prints</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody id="ordersTableBody">
            {isLoading && (
              <tr>
                <td colSpan={11} style={{ textAlign: "center", padding: 20, color: "#888" }}>
                  Loading…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={11} style={{ textAlign: "center", padding: 20, color: "#c0392b" }}>
                  {(error as Error).message}
                </td>
              </tr>
            )}
            {records.map((record, index) => {
              const id = String(record.rowid);
              const state = overrides[id] ?? bucketToState(record.return_status_bucket);
              const normOpt = record.return_option_normalized || "";
              const { cls: printsCls, blankLabel } = printsSelectClass(record);
              const rowHighlight = findLower && JSON.stringify(record).toLowerCase().includes(findLower);
              return (
                <tr
                  key={id}
                  data-index={index}
                  data-id={id}
                  className={[rowClass(state), rowHighlight ? "highlight" : ""].filter(Boolean).join(" ")}
                >
                  <td style={{ width: 35, textAlign: "center", color: "#888", fontSize: 12 }}>
                    {record.daily_order_num == null ? "" : record.daily_order_num}
                    {record.has_updates ? (
                      <span
                        className="edited-dot"
                        title="This order has logged changes — click to view history"
                        onClick={() => onOpenHistory(String(record["invoice-id"] || record.rowid))}
                      >
                        {" ●"}
                      </span>
                    ) : null}
                  </td>
                  <td style={{ width: 90 }}>
                    <input
                      type="text"
                      className="editable-field orders-field"
                      data-field="-date"
                      defaultValue={record["-date"] || ""}
                      style={{ width: "100%" }}
                      onBlur={(e) => saveField(record, "-date", e.target)}
                      onKeyDown={(e) => onInputKeyDown(e, record, "-date")}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="editable-field orders-field"
                      data-field="full-name"
                      defaultValue={record["full-name"] || ""}
                      onBlur={(e) => saveField(record, "full-name", e.target)}
                      onKeyDown={(e) => onInputKeyDown(e, record, "full-name")}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="editable-field orders-field"
                      data-field="roll-types"
                      defaultValue={record["roll-types"] || ""}
                      onBlur={(e) => saveField(record, "roll-types", e.target)}
                      onKeyDown={(e) => onInputKeyDown(e, record, "roll-types")}
                    />
                  </td>
                  <td style={{ width: 60 }}>
                    <input
                      type="number"
                      className="editable-field orders-field"
                      data-field="amount-of-rolls"
                      defaultValue={(record["amount-of-rolls"] as string) || ""}
                      style={{ width: "100%" }}
                      onBlur={(e) => saveField(record, "amount-of-rolls", e.target)}
                      onKeyDown={(e) => onInputKeyDown(e, record, "amount-of-rolls")}
                    />
                  </td>
                  <td style={{ width: 280 }}>
                    <input
                      type="text"
                      className="editable-field orders-field"
                      data-field="Twin Check Stickers"
                      defaultValue={record["Twin Check Stickers"] || ""}
                      style={{ width: "100%" }}
                      onBlur={(e) => saveField(record, "Twin Check Stickers", e.target)}
                      onKeyDown={(e) => onInputKeyDown(e, record, "Twin Check Stickers")}
                    />
                  </td>
                  <td>
                    <select
                      className="editable-field orders-field"
                      data-field="return-option"
                      defaultValue={normOpt}
                      onChange={(e) => saveField(record, "return-option", e.target)}
                    >
                      <option value="">Select...</option>
                      <option value="no-negs">No Negs</option>
                      <option value="pickup-cut">Pickup Cut</option>
                      <option value="uncut">Uncut</option>
                      <option value="mail-back">Mail Back</option>
                    </select>
                  </td>
                  <td>
                    <select
                      className="editable-field orders-field"
                      data-field="return-status"
                      defaultValue={(record["return-status"] as string) || ""}
                      onChange={(e) => saveField(record, "return-status", e.target)}
                    >
                      <option value="">Select...</option>
                      <option value="cut">Cut</option>
                      <option value="prepped">Prepped</option>
                      <option value="ready to mail">Ready to Mail</option>
                      <option value="tossed">Tossed</option>
                      <option value="picked-up">Picked Up</option>
                      <option value="mailed">Mailed</option>
                      <option value="no show - tossed">No Show - Tossed</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="editable-field orders-field"
                      data-field="notes"
                      defaultValue={record.notes || ""}
                      title={record.notes || ""}
                      onBlur={(e) => saveField(record, "notes", e.target)}
                      onKeyDown={(e) => onInputKeyDown(e, record, "notes")}
                    />
                  </td>
                  <td style={{ width: 150, textAlign: "center" }}>
                    <select
                      className={printsCls}
                      data-field="print-dispatch-status"
                      defaultValue={(record["print-dispatch-status"] as string) || ""}
                      onChange={(e) => saveField(record, "print-dispatch-status", e.target)}
                    >
                      <option value="">{blankLabel}</option>
                      <option value="pickup">Pickup</option>
                      <option value="dropship-pending">Drop-ship pending</option>
                      <option value="dropship-complete">Drop-ship complete</option>
                    </select>
                  </td>
                  <td>
                    <a
                      href="#"
                      className="view-link"
                      onClick={(e) => {
                        e.preventDefault();
                        viewOrder(record);
                      }}
                    >
                      View
                    </a>{" "}
                    <a
                      href="#"
                      className="view-link"
                      onClick={(e) => {
                        e.preventDefault();
                        onOpenHistory(String(record["invoice-id"] || record.rowid), record["full-name"] || undefined);
                      }}
                    >
                      History
                    </a>{" "}
                    <button className="delete-button" onClick={() => deleteRecord(id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
